import React, { useState, useEffect, useCallback } from 'react';
import { buyersAPI, outreachAPI, leadScoringAPI, emailVerificationAPI, billingAPI, creditsAPI, researchAPI } from '../services/api';
import { Edit, Save, X, Users, Mail, MapPin, Phone, Search, ChevronRight } from 'lucide-react';
import TemplateSelector from './TemplateSelector';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import GroupSelector from './GroupSelector';
import ProspectExtras from './ProspectExtras';
import ProspectDetailPanel, { Pill, GroupPill, GroupMorePill } from './ProspectDetailPanel';
import { safeUrl } from '../utils/safeUrl';
import toast from '../utils/toast';
import GroupManagementModal from './GroupManagementModal';
import CsvUploadResults from './CsvUploadResults';
import ProspectsActionToolbar from './ProspectsActionToolbar';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import usePlans from '../hooks/usePlans';
import TopUpPacks from './TopUpPacks';
import ResearchRunDialog from './ResearchRunDialog';
import { statusLabel, priorityTone, hasActive, bestResult } from '../utils/research';
import './ResearchPanel.css';
import './BuyersList.css';
import './ProspectsPage.css';

function BuyersList() {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [totalBuyers, setTotalBuyers] = useState(0);

  // Search (debounced so typing doesn't fire an API call on every keystroke)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filters
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [status, setStatus] = useState('');
  const [contactMethod, setContactMethod] = useState('');
  const [hasWebsite, setHasWebsite] = useState('');
  const [dncStatus, setDncStatus] = useState('');
  const [phoneType, setPhoneType] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('');
  const [phoneValidity, setPhoneValidity] = useState('');
  const [filterOptions, setFilterOptions] = useState({
    industries: [],
    locations: [],
    statuses: [],
    phone_types: [],
    phone_countries: []
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const viewMode = 'stacked'; // locked to stacked view
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Lead scoring
  const [sortByScore, setSortByScore] = useState(false);
  const [scoreFilter, setScoreFilter] = useState('all'); // all, high, medium, low

  // Selection
  const [selectedBuyers, setSelectedBuyers] = useState([]);

  // AI research: ids queued from the toolbar (null = dialog closed) and the best result per visible prospect
  const [researchIds, setResearchIds] = useState(null);
  const [researchByBuyer, setResearchByBuyer] = useState({});
  const [researchTick, setResearchTick] = useState(0);

  // Detail slide-over: id of the prospect whose panel is open (null = closed)
  const [openProspectId, setOpenProspectId] = useState(null);
  // Prospect id whose Verify email was started from the panel (so its result is shown inside the panel)
  const [panelVerifyFor, setPanelVerifyFor] = useState(null);

  // Outreach modal
  const [showOutreachModal, setShowOutreachModal] = useState(false);
  const [outreachType, setOutreachType] = useState('email');

  // Confirm dialog
  const [confirmAsync, confirmAsyncDialog] = useConfirm();
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  // DNC modal
  const [dncModal, setDncModal] = useState({ open: false, reason: 'Replied STOP' });
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [currentBuyerForTemplate, setCurrentBuyerForTemplate] = useState(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(null);

  // CSV Upload
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [csvFileInfo, setCsvFileInfo] = useState(null);
  const [showColumnInfo, setShowColumnInfo] = useState(false);
  const [csvResults, setCsvResults] = useState(null);
  const [showCsvResults, setShowCsvResults] = useState(false);

  // Group management
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showGroupManagement, setShowGroupManagement] = useState(false);

  // Email verification
  const [verifyingBuyerId, setVerifyingBuyerId] = useState(null);
  // Single-prospect verify outcome. Kept apart from the page-level error, which hides the list.
  const [verifyError, setVerifyError] = useState(null);
  const [verifySuccess, setVerifySuccess] = useState(null);
  const [bulkVerifying, setBulkVerifying] = useState(false);

  // Token balance
  const [toppingUp, setToppingUp] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const { user } = useAuth();
  const { credits, refreshCredits } = useCredits();
  const { topups, loading: plansLoading, error: plansError, reload: reloadPlans } = usePlans();
  const tokenBalance = credits ? { total: credits.total, monthly: credits.monthly, bonus: credits.topup } : null;

  useEffect(() => {
    loadFilterOptions();
    refreshCredits();
  }, []);

  // AI research chips: best-effort. One status call for the ids on the displayed page (max 200);
  // any failure just hides the chips. While a row is pending/running, poll every 10 s for up to 5 min.
  const researchIdsKey = buyers.slice(0, 200).map(b => b.id).join(',');
  useEffect(() => {
    if (!researchIdsKey) {
      setResearchByBuyer(prev => (Object.keys(prev).length ? {} : prev));
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    const load = async () => {
      try {
        const res = await researchAPI.statuses(researchIdsKey.split(',').map(Number));
        if (cancelled) return;
        const rows = Array.isArray(res.data?.results) ? res.data.results : [];
        const grouped = {};
        rows.forEach(r => {
          if (r && r.buyer_id != null) (grouped[r.buyer_id] = grouped[r.buyer_id] || []).push(r);
        });
        const next = {};
        Object.keys(grouped).forEach(id => {
          const list = grouped[id]; // newest first
          next[id] = bestResult(list)
            || list.find(r => r.status === 'pending' || r.status === 'running')
            || list.find(r => r.status === 'failed')
            || null;
        });
        setResearchByBuyer(next);
        if (hasActive(rows) && Date.now() - startedAt < 5 * 60 * 1000) {
          timer = setTimeout(load, 10000);
        }
      } catch (err) {
        if (!cancelled) setResearchByBuyer(prev => (Object.keys(prev).length ? {} : prev));
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [researchIdsKey, researchTick]);

  const renderResearchChip = (buyerId) => {
    const r = researchByBuyer[buyerId];
    if (!r) return null;
    const complete = r.status === 'complete';
    const tone = complete ? priorityTone(r.priority) : (r.status === 'failed' ? 'failed' : 'neutral');
    return (
      <span className={`research-chip research-chip--${tone}`} title={statusLabel(r)}>
        {complete ? `AI ${r.score}` : statusLabel(r)}
      </span>
    );
  };

  // Fetch when page, size, or client-side sort/filter changes
  useEffect(() => {
    loadBuyers();
  }, [currentPage, pageSize, sortByScore, scoreFilter]);

  // On server-side filter change: reset to page 1 and fetch
  // If already on page 1, fetch directly (setCurrentPage(1) won't trigger above effect)
  // If on a later page, reset page — the effect above will fire when currentPage settles to 1
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      loadBuyers();
    }
  }, [debouncedSearchTerm, industry, location, status, contactMethod, dncStatus, selectedGroups, phoneType, phoneCountry, phoneValidity, hasWebsite]);

  const handleTopUp = async (pkg) => {
    setToppingUp(true);
    try {
      const res = await billingAPI.topUpTokens(pkg);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setError('Checkout link missing from the server response. Please try again or contact support.');
      }
    } catch (err) {
      // Same wording as the Billing page: show the server's reason, and for a
      // product that is not set up in ThriveCart ('... not configured') add the support hint.
      const data = err?.response?.data || {};
      const serverError = typeof data.error === 'string' ? data.error : '';
      const serverMessage = typeof data.message === 'string' ? data.message : '';
      const notConfigured = /not configured/i.test(serverError) || /contact support/i.test(serverMessage);
      if (notConfigured) {
        setError(`${serverError || 'This product is not configured'}. This purchase is not set up yet. Please contact support so we can fix it.`);
      } else if (serverMessage || serverError) {
        setError(serverMessage || serverError);
      } else if (!err?.response) {
        setError('Could not reach the server. Check your connection and try again.');
      } else {
        setError('Could not open checkout. Please try from the Billing page.');
      }
    } finally {
      setToppingUp(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const response = await buyersAPI.getFilterOptions();
      const options = response.data?.options || {};
      setFilterOptions({
        industries: options.industries || [],
        locations: options.locations || [],
        statuses: options.statuses || [],
        phone_types: options.phone_types || ['mobile', 'landline', 'voip', 'unknown', 'other'],
        phone_countries: options.phone_countries || []
      });
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const loadBuyers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (industry) params.industry = industry;
      if (location) params.location = location;
      if (status) params.status = status;
      if (contactMethod) params.contact_method = contactMethod;
      if (hasWebsite) params.has_website = hasWebsite;
      if (dncStatus) params.dnc_status = dncStatus;
      if (selectedGroups.length > 0) {
        // If 'none' is in the selection, send group_ids=none (special backend value for ungrouped)
        const hasNoGroup = selectedGroups.includes('none');
        params.group_ids = hasNoGroup ? 'none' : selectedGroups.join(',');
      }
      if (phoneType) params.phone_type = phoneType;
      if (phoneCountry) params.phone_country = phoneCountry;
      if (phoneValidity) params.phone_valid = phoneValidity;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      params.limit = pageSize;
      params.offset = (currentPage - 1) * pageSize;

      const response = await buyersAPI.getAll(params);
      let buyersWithScores = response.data.buyers || [];
      const serverTotalCount = response.data.total_count ?? response.data.count ?? 0;

      // Calculate lead scores for each buyer
      buyersWithScores = buyersWithScores.map(buyer => ({
        ...buyer,
        leadScore: calculateLeadScore(buyer)
      }));

      // Apply lead score filter (client-side, within current page)
      if (scoreFilter !== 'all') {
        buyersWithScores = buyersWithScores.filter(buyer => {
          const score = buyer.leadScore;
          if (scoreFilter === 'high') return score >= 70;
          if (scoreFilter === 'medium') return score >= 40 && score < 70;
          if (scoreFilter === 'low') return score < 40;
          return true;
        });
      }

      // Sort by lead score if requested
      if (sortByScore) {
        buyersWithScores.sort((a, b) => b.leadScore - a.leadScore);
      }

      setBuyers(buyersWithScores);
      setTotalCount(serverTotalCount);
      setTotalBuyers(serverTotalCount);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      console.error('Failed to load buyers:', err);

      // Determine error message based on error type
      let errorMessage = 'Failed to load buyers';
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again in a few moments.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Buyers service not available. Please contact support.';
      } else if (!navigator.onLine) {
        errorMessage = 'You are offline. Please check your internet connection.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
    loadBuyers();
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setUploadingCSV(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await buyersAPI.uploadCSV(file);
      const summary = response.data.summary;

      // Set CSV results for color-coded display
      setCsvResults({
        added: summary.added || 0,
        updated: summary.updated || 0,
        duplicates: summary.duplicates_skipped || 0,
        invalid: summary.invalid_rows || 0,
        failed: summary.failed || 0
      });

      // Show results modal
      setShowCsvResults(true);

      // Refresh data
      loadBuyers();

      // Clear file input
      event.target.value = '';

      // Store upload info
      setCsvFileInfo(summary);

    } catch (err) {
      console.error('CSV upload error:', err);
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to upload CSV: ${errorMsg}`);
    } finally {
      setUploadingCSV(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await buyersAPI.downloadCSVTemplate();

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prospect_template_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('CSV template downloaded successfully');
    } catch (err) {
      console.error('Template download error:', err);
      setError('Failed to download CSV template');
    }
  };

  const handleToggleColumnInfo = async () => {
    if (!showColumnInfo && !csvFileInfo) {
      try {
        const response = await buyersAPI.getCSVColumnsInfo();
        setCsvFileInfo(response.data.column_info);
      } catch (err) {
        console.error('Failed to load column info:', err);
        setError('Failed to load column information');
        return;
      }
    }
    setShowColumnInfo(!showColumnInfo);
  };

  /**
   * Calculate Lead Score (Client-Side Algorithm)
   *
   * This is a simplified scoring system. For more advanced scoring,
   * consider using the backend LeadScoring service (backend/services/lead_scoring_enhanced.py)
   * which includes:
   * - Industry-specific weights with seasonal multipliers
   * - Location bonuses for UK regions and high-value areas
   * - Contact quality assessment (professional vs personal email)
   * - Business maturity indicators
   * - Engagement tracking
   * - Data completeness scoring
   *
   * Current Client-Side Scoring Breakdown:
   * - Industry: 15-25 points (high-value industries like roofing, plumbing get 25)
   * - Contact Methods: 10-20 points (both email+phone = 20, one = 10)
   * - Website: 15 points
   * - Status: 10-20 points (new = 20, contacted = 10)
   * - Location: 10 points (UK businesses)
   * - Contact History: 10 points (if previously contacted)
   * - Maximum Score: 100 (capped)
   */
  const calculateLeadScore = (buyer) => {
    let score = 0;

    // Industry scoring
    const highValueIndustries = ['roofing', 'plumbing', 'electrical', 'hvac'];
    if (highValueIndustries.includes(buyer.industry?.toLowerCase())) {
      score += 25;
    } else {
      score += 15;
    }

    // Contact method scoring
    if (buyer.email && buyer.phone) {
      score += 20; // Both contact methods
    } else if (buyer.email || buyer.phone) {
      score += 10; // One contact method
    }

    // Website scoring
    if (buyer.website) {
      score += 15;
    }

    // Status scoring
    if (buyer.status === 'new') {
      score += 20;
    } else if (buyer.status === 'contacted') {
      score += 10;
    }

    // Location scoring (local businesses often convert better)
    if (buyer.location && buyer.location.includes('UK')) {
      score += 10;
    }

    // Contact history scoring
    if (buyer.contact_history && buyer.contact_history.length > 0) {
      score += 10;
    }

    return Math.min(100, score);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'var(--success-solid)'; // Green for high scores
    if (score >= 40) return 'var(--warn)'; // Yellow for medium scores
    return 'var(--danger)'; // Red for low scores
  };

  const toggleSelectBuyer = (buyerId) => {
    setSelectedBuyers(prev =>
      prev.includes(buyerId)
        ? prev.filter(id => id !== buyerId)
        : [...prev, buyerId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedBuyers.length === buyers.length) {
      setSelectedBuyers([]);
    } else {
      setSelectedBuyers(buyers.map(b => b.id));
    }
  };

  const handleTemplateSelect = (templateData) => {
    setEmailSubject(templateData.subject);
    setEmailMessage(templateData.message);
    setShowTemplateSelector(false);
  };

  const handleOpenTemplateSelector = () => {
    // Get the first selected buyer for variable substitution preview
    const firstBuyerId = selectedBuyers[0];
    const firstBuyer = buyers.find(b => b.id === firstBuyerId);
    setCurrentBuyerForTemplate(firstBuyer);
    setShowTemplateSelector(true);
  };

  const handleSendOutreach = async () => {
    if (selectedBuyers.length === 0) {
      setError('Please select at least one buyer');
      return;
    }

    try {
      setLoading(true);
      let response;

      if (outreachType === 'email') {
        response = await outreachAPI.sendEmail({
          buyer_ids: selectedBuyers,
          subject: emailSubject,
          message: emailMessage,
          provider: 'smtp'
        });
      } else {
        response = await outreachAPI.sendSMS({
          buyer_ids: selectedBuyers,
          message: smsMessage
        });
      }

      const dncMsg = response.data.results.dnc_skipped > 0
        ? ` (${response.data.results.dnc_skipped} DNC skipped)`
        : '';
      setSuccess(`Successfully sent ${outreachType} to ${response.data.results.sent} buyers${dncMsg}`);
      setError(null);
      setShowOutreachModal(false);
      setSelectedBuyers([]);
      loadBuyers();
    } catch (err) {
      setError(`Failed to send ${outreachType}: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditBuyer = (buyer) => {
    setEditingBuyer({
      ...buyer,
      do_not_contact: !!buyer.do_not_contact
    });
    setShowEditModal(true);
  };

  // Shared by the Edit Buyer modal and the detail panel: same payload, same call.
  const persistBuyerEdit = async (buyerData) => {
    const { id, groups, leadScore, leadScoreCategory, ...payload } = buyerData;
    await buyersAPI.update(id, payload);
  };

  // Detail panel save (edits and the Do Not Contact flag): send the full buyer with the
  // changed fields (as the modal does), then refetch that prospect so the values the
  // backend derives (phone type/validity/country, do_not_contact_date) are current, and
  // update the row in place so the list and the open panel show them without reloading
  // the page. If the refetch fails, the phone details are cleared until the next load.
  const handlePanelSave = async (buyer, changes) => {
    const merged = { ...buyer, ...changes, do_not_contact: !!(changes.do_not_contact ?? buyer.do_not_contact) };
    try {
      await persistBuyerEdit(merged);
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'Failed to update buyer');
    }
    let fresh = null;
    try {
      const response = await buyersAPI.getById(buyer.id);
      fresh = response.data?.buyer || null;
    } catch (err) {
      console.error('Failed to reload prospect after save:', err);
    }
    let local;
    if (fresh) {
      // The single-prospect endpoint has no groups; keep the list's copy of them
      local = { ...buyer, ...fresh, groups: buyer.groups };
    } else if (Object.prototype.hasOwnProperty.call(changes, 'phone')) {
      local = { ...merged, phone_e164: null, phone_type: null, phone_valid: null, phone_country: null };
    } else {
      local = merged;
    }
    setBuyers(prev => prev.map(b => (b.id === buyer.id ? { ...local, leadScore: calculateLeadScore(local) } : b)));
    setPanelVerifyFor(null);
    setSuccess('Buyer updated successfully');
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingBuyer) return;

    try {
      setLoading(true);
      await persistBuyerEdit(editingBuyer);
      setSuccess('Buyer updated successfully');
      setError(null);
      setShowEditModal(false);
      setEditingBuyer(null);
      loadBuyers();
    } catch (err) {
      setError(`Failed to update buyer: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingBuyer(null);
  };

  const handleDeleteSelected = async () => {
    if (selectedBuyers.length === 0) {
      setError('Please select at least one buyer to delete');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Buyers',
      message: `Are you sure you want to delete ${selectedBuyers.length} buyer(s)? This action cannot be undone.`,
      onConfirm: handleDeleteConfirm
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await buyersAPI.bulkDelete(selectedBuyers);
      setSuccess(`Successfully deleted ${response.data.deleted} buyer(s)`);
      setError(null);
      setSelectedBuyers([]);
      loadBuyers();
    } catch (err) {
      setError(`Failed to delete buyers: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDNC = () => {
    if (selectedBuyers.length === 0) {
      setError('Please select at least one buyer to mark as Do Not Contact');
      return;
    }
    setDncModal({ open: true, reason: 'Replied STOP' });
  };

  const handleMarkDNCConfirm = async () => {
    try {
      setLoading(true);
      const response = await buyersAPI.markDNC(selectedBuyers, dncModal.reason || 'Manual request');
      setSuccess(`Successfully marked ${response.data.marked} buyer(s) as Do Not Contact`);
      setError(null);
      setSelectedBuyers([]);
      setDncModal({ open: false, reason: 'Replied STOP' });
      loadBuyers();
    } catch (err) {
      setError(`Failed to mark DNC: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDNC = async () => {
    if (selectedBuyers.length === 0) {
      setError('Please select at least one buyer to remove DNC status');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Remove DNC Status',
      message: `Remove Do Not Contact status from ${selectedBuyers.length} buyer(s)?`,
      onConfirm: handleRemoveDNCConfirm
    });
  };

  const handleRemoveDNCConfirm = async () => {
    try {
      setLoading(true);
      const response = await buyersAPI.removeDNC(selectedBuyers);
      setSuccess(`Successfully removed DNC status from ${response.data.removed} buyer(s)`);
      setError(null);
      setSelectedBuyers([]);
      loadBuyers();
    } catch (err) {
      setError(`Failed to remove DNC: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // fromPanel: the detail panel shows the outcome itself; otherwise it is shown as a toast.
  const handleVerifyEmail = async (buyerId, method = 'hunter', fromPanel = false) => {
    if (!buyerId) return;

    setVerifyError(null);
    setVerifySuccess(null);
    setVerifyingBuyerId(buyerId);

    try {
      const response = await buyersAPI.verifyEmail(buyerId, method);
      const verification = response.data?.verification;

      if (verification) {
        setBuyers(prev =>
          prev.map(buyer =>
            buyer.id === buyerId
              ? {
                  ...buyer,
                  email_verification_status: verification.status,
                  email_verification_score: verification.score,
                  last_verified_at: verification.verified_at
                }
              : buyer
          )
        );
        // Show method used in success message
        const methodLabel = verification.method === 'hunter' ? '🔍 Hunter.io' : verification.method === 'smtp' ? 'SMTP' : 'Basic';
        const message = `${methodLabel} Verification: ${verification.status || 'complete'} (score ${verification.score ?? 'n/a'})`;
        setVerifySuccess(message);
        if (!fromPanel) toast.success(message);
      } else {
        setVerifySuccess('Verification completed.');
        if (!fromPanel) toast.success('Verification completed.');
      }
      refreshCredits();
    } catch (err) {
      const message = `Email verification failed: ${err.response?.data?.error || err.message}`;
      setVerifyError(message);
      if (!fromPanel) toast.error(message);
    } finally {
      setVerifyingBuyerId(null);
    }
  };

  const handleBulkVerifyEmails = async (method = 'hunter') => {
    if (selectedBuyers.length === 0) return;

    setError(null);
    setSuccess(null);

    try {
      const est = (await creditsAPI.estimateVerification(selectedBuyers)).data;
      if (est.to_charge === 0) {
        setSuccess(`Nothing to check: ${est.skipped_recent} already verified in the last 30 days, ${est.no_email} without an email. No credits used.`);
        return;
      }
      const lines = [`This will use up to ${est.to_charge} credit${est.to_charge === 1 ? '' : 's'} (you have ${est.balance.toLocaleString()}).`];
      if (est.skipped_recent > 0) lines.push(`${est.skipped_recent} already verified in the last 30 days are free.`);
      if (est.balance === 0 && est.to_charge > 0) {
        setError('You have no credits left to verify emails. Use "+ Buy Credits" to top up.');
        return;
      }
      if (est.to_charge > est.balance) {
        const skipped = est.to_charge - est.balance;
        lines.push(`You only have enough credits for ${est.balance}; the remaining ${skipped} paid check${skipped === 1 ? '' : 's'} will be skipped.`);
      }
      if (!(await confirmAsync({
        title: 'Verify emails',
        message: lines.join('\n'),
        confirmText: 'Verify emails',
        variant: 'primary'
      }))) return;
    } catch (err) {
      // Estimate is advisory: if it fails, fall through to the normal start (the server still enforces credits)
    }

    setBulkVerifying(true);

    try {
      const response = await emailVerificationAPI.startBulkVerify(selectedBuyers, method);
      const jobId = response.data.job_id;
      const total = response.data.total;

      // Store job in localStorage so Layout can notify across page navigation
      localStorage.setItem('pending_verification_job', JSON.stringify({
        jobId,
        total,
        startedAt: Date.now()
      }));

      setSuccess(`Verification started for ${total} prospects — you can navigate away and we'll notify you when it's done.`);
      setBulkVerifying(false);
      refreshCredits();

    } catch (err) {
      setError(`Failed to start verification: ${err.response?.data?.error || err.message}`);
      setBulkVerifying(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return dateString;
    return parsed.toLocaleString();
  };

  const getVerificationMeta = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'deliverable':
        return { label: 'Deliverable', className: 'status-deliverable', tone: 'success' };
      case 'accept_all':
        return { label: 'Accept-All', className: 'status-accept', tone: 'warn' };
      case 'rejected':
        return { label: 'Rejected', className: 'status-rejected', tone: 'danger' };
      case 'risky':
        return { label: 'Risky', className: 'status-risky', tone: 'warn' };
      case 'unknown':
        return { label: 'Unknown', className: 'status-unknown', tone: 'neutral' };
      default:
        return { label: 'Unverified', className: 'status-unverified', tone: 'muted' };
    }
  };

  const phoneTypeMetaMap = {
    mobile: { label: 'Mobile', className: 'phone-type-mobile' },
    landline: { label: 'Landline', className: 'phone-type-landline' },
    voip: { label: 'VoIP', className: 'phone-type-voip' },
    unknown: { label: 'Unknown', className: 'phone-type-unknown' },
    other: { label: 'Other', className: 'phone-type-unknown' }
  };

  const getPhoneTypeMeta = (type) => {
    if (!type) return phoneTypeMetaMap.unknown;
    const key = type.toLowerCase();
    return phoneTypeMetaMap[key] || phoneTypeMetaMap.unknown;
  };

  const getContactStatusBadge = (status) => {
    const normalized = (status || '').toLowerCase();
    switch (normalized) {
      case 'delivered':
        return 'badge-success';
      case 'sent':
        return 'badge-primary';
      case 'opened':
        return 'badge-indigo';
      case 'clicked':
        return 'badge-warning';
      case 'bounced':
        return 'badge-danger';
      case 'failed':
        return 'badge-danger';
      case 'skipped':
        return 'badge-warning';
      case 'scheduled':
        return 'badge-info';
      default:
        return 'badge-info';
    }
  };

  const canCallBuyer = (buyer) => {
    if (!buyer) return false;
    if (buyer.do_not_contact) return false;
    if (!buyer.phone_valid) return false;
    if (!(buyer.phone_e164 || buyer.phone)) return false;
    return true;
  };

  const handleNativeCall = (buyer) => {
    if (!buyer || !canCallBuyer(buyer)) return;
    const tel = buyer.phone_e164 || buyer.phone;
    if (tel) {
      window.location.href = `tel:${tel}`;
    }
  };

  // Pill colour for a contact status, following the same mapping as getContactStatusBadge
  const contactStatusTone = (status) => {
    const toneByBadge = {
      'badge-success': 'success',
      'badge-primary': 'info',
      'badge-indigo': 'tag',
      'badge-warning': 'warn',
      'badge-danger': 'danger',
      'badge-info': 'info'
    };
    return toneByBadge[getContactStatusBadge(status)] || 'info';
  };

  // Display facts shared by the prospect card and the detail panel
  const getProspectMeta = (buyer) => {
    let statusPill;
    if (buyer.do_not_contact) {
      statusPill = {
        label: 'DNC',
        tone: 'danger',
        title: `Reason: ${buyer.do_not_contact_reason || 'N/A'}\nDate: ${formatDate(buyer.do_not_contact_date)}`
      };
    } else if (buyer.last_contact_status) {
      statusPill = { label: buyer.last_contact_status, tone: contactStatusTone(buyer.last_contact_status), title: 'Last contact status' };
    } else {
      statusPill = { label: 'Active', tone: 'success', title: 'Can be contacted' };
    }
    return {
      verificationMeta: getVerificationMeta(buyer.email_verification_status),
      phoneMeta: getPhoneTypeMeta(buyer.phone_type),
      normalizedPhone: buyer.phone_e164 || buyer.phone || '-',
      phoneValidityState: buyer.phone_valid === null || buyer.phone_valid === undefined ? null : Boolean(buyer.phone_valid),
      contactStatusTone: contactStatusTone(buyer.last_contact_status),
      statusPill,
      canCall: canCallBuyer(buyer)
    };
  };

  const openProspect = (buyerId) => {
    setPanelVerifyFor(null);
    setOpenProspectId(buyerId);
  };
  const closeProspect = useCallback(() => {
    setPanelVerifyFor(null);
    setOpenProspectId(null);
  }, []);

  // Verify result for the open panel, from the verify-only messages set by handleVerifyEmail
  const getPanelVerifyNotice = (buyerId) => {
    if (panelVerifyFor !== buyerId) return null;
    if (verifyingBuyerId === buyerId) return { kind: 'pending', text: 'Verifying…' };
    if (verifyError) return { kind: 'error', text: verifyError };
    if (verifySuccess) return { kind: 'success', text: verifySuccess };
    return null;
  };

  const handleCardKeyDown = (e, buyerId) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openProspect(buyerId);
    }
  };

  const openBuyer = openProspectId === null ? null : buyers.find(b => b.id === openProspectId) || null;

  // Close the panel if its prospect is no longer in the loaded page (deleted, filtered out, page changed)
  useEffect(() => {
    if (openProspectId !== null && !loading && !buyers.some(b => b.id === openProspectId)) {
      setOpenProspectId(null);
    }
  }, [openProspectId, buyers, loading]);

  // With server-side pagination, buyers from the API are already the current page
  const filteredBuyers = buyers; // kept for compatibility with toolbar props
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pagedBuyers = buyers; // server already returned the correct page

  const isPageSelected = pagedBuyers.length > 0 && pagedBuyers.every(b => selectedBuyers.includes(b.id));

  const toggleSelectPage = () => {
    if (isPageSelected) {
      const pageIds = new Set((pagedBuyers || []).map(b => b.id));
      setSelectedBuyers(prev => prev.filter(id => !pageIds.has(id)));
    } else {
      setSelectedBuyers(prev => {
        const next = new Set(prev);
        pagedBuyers.forEach(b => next.add(b.id));
        return Array.from(next);
      });
    }
  };

  const getPageItems = () => {
    const items = [];
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i += 1) items.push(i);
      return items;
    }

    const left = Math.max(2, safePage - 1);
    const right = Math.min(totalPages - 1, safePage + 1);

    items.push(1);
    if (left > 2) items.push('ellipsis-left');
    for (let i = left; i <= right; i += 1) items.push(i);
    if (right < totalPages - 1) items.push('ellipsis-right');
    items.push(totalPages);
    return items;
  };

  const pageItems = getPageItems();

  return (
    <div className="prospects-page">
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <Users className="title-icon" />
            Prospects Management
          </h1>
          <p className="page-description">
            Manage and track all your business prospects
          </p>
        </div>

        {/* Email Verification Token Balance */}
        {(
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: !tokenBalance ? 'var(--surface-subtle)' : tokenBalance.total === 0 ? 'var(--danger-soft)' : tokenBalance.total < 50 ? 'var(--warn-soft)' : 'var(--success-soft)',
            border: `1px solid ${!tokenBalance ? 'var(--border-color)' : tokenBalance.total === 0 ? 'rgba(220, 38, 38, 0.2)' : tokenBalance.total < 50 ? 'rgba(180, 83, 9, 0.2)' : 'rgba(21, 128, 61, 0.2)'}`,
            borderRadius: '10px',
            padding: '10px 16px',
            flexShrink: 0,
          }}>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                Credits
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontSize: '1.4rem', fontWeight: 700, color: !tokenBalance ? 'var(--text-secondary)' : tokenBalance.total === 0 ? 'var(--danger)' : tokenBalance.total < 50 ? 'var(--warn)' : 'var(--success)', lineHeight: 1 }}>
                  {tokenBalance ? tokenBalance.total.toLocaleString() : '—'}
                </span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>remaining</span>
              </div>
              {tokenBalance && (tokenBalance.monthly > 0 || tokenBalance.bonus > 0) && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {tokenBalance.monthly > 0 && <span>{tokenBalance.monthly.toLocaleString()} monthly</span>}
                  {tokenBalance.monthly > 0 && tokenBalance.bonus > 0 && <span> · </span>}
                  {tokenBalance.bonus > 0 && <span>{tokenBalance.bonus.toLocaleString()} top-up</span>}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowTokenModal(true)}
              style={{
                background: 'var(--primary-color)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 14px',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              + Buy Credits
            </button>
          </div>
        )}
      </div>

      <div className="search-box" style={{ marginBottom: '1rem' }}>
        <Search className="search-icon" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search prospects by name, email, or phone..."
        />
      </div>

      {error && (
        <div className="alert alert-error" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <strong>Error:</strong> {error}
            {retryCount > 0 && <span style={{ marginLeft: '8px', fontSize: '0.9em', color: 'var(--text-secondary)' }}>(Attempt {retryCount})</span>}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleRetry}
              className="btn btn-primary"
              disabled={loading}
              style={{ fontSize: '0.9em', padding: '4px 12px' }}
            >
              Retry
            </button>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-secondary"
              style={{ fontSize: '0.9em', padding: '4px 12px' }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      )}

      {success && <div className="alert alert-success">{success}</div>}

      {loading && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '1.1rem', marginBottom: '8px' }}>Loading buyers...</div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            {retryCount > 0 ? `Retrying... (Attempt ${retryCount})` : 'Fetching data from server'}
          </div>
        </div>
      )}

      {!loading && !error && buyers.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '1.1rem', marginBottom: '8px', color: 'var(--text-secondary)' }}>No buyers found</div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            Try adjusting your search/filters or {searchTerm || industry || location || status || contactMethod || dncStatus || phoneType || phoneCountry || phoneValidity || hasWebsite ? 'clearing them' : 'adding new buyers'}
          </div>
          {(searchTerm || industry || location || status || contactMethod || dncStatus || selectedGroups.length > 0 || phoneType || phoneCountry || phoneValidity || hasWebsite) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setIndustry('');
                setLocation('');
                setStatus('');
                setContactMethod('');
                setDncStatus('');
                setPhoneType('');
                setPhoneCountry('');
                setPhoneValidity('');
                setHasWebsite('');
                setSelectedGroups([]);
                setRetryCount(0);
              }}
              className="btn btn-secondary"
              style={{ marginTop: '12px' }}
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* Filter Drawer */}
      {showFilterPanel && (
        <div
          className="filter-drawer-backdrop"
          onClick={() => setShowFilterPanel(false)}
        />
      )}
      <div className={`filter-drawer ${showFilterPanel ? 'open' : ''}`}>
        <div className="filter-drawer-header">
          <div>
            <h3>Filters</h3>
            <p>Refine the list and tags.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className="btn btn-secondary"
              style={{ padding: '8px' }}
              onClick={() => setShowFilterPanel(false)}
              type="button"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="filter-drawer-body">
          <div className="filter-grid">
            <div className="form-group">
              <label className="form-label">Industry</label>
              <select
                className="form-select"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              >
                <option value="">All Industries</option>
                {filterOptions.industries.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Location</label>
              <select
                className="form-select"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                <option value="">All Locations</option>
                {filterOptions.locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="responded">Responded</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Contact Method</label>
              <select
                className="form-select"
                value={contactMethod}
                onChange={(e) => setContactMethod(e.target.value)}
              >
                <option value="">All Buyers</option>
                <option value="email">Has Email</option>
                <option value="sms">Has SMS (Mobile)</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Website</label>
              <select
                className="form-select"
                value={hasWebsite}
                onChange={(e) => setHasWebsite(e.target.value)}
              >
                <option value="">Website: any</option>
                <option value="yes">Has website</option>
                <option value="no">No website</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">DNC Status</label>
              <select
                className="form-select"
                value={dncStatus}
                onChange={(e) => setDncStatus(e.target.value)}
              >
                <option value="">All Buyers</option>
                <option value="active">Active (Can Contact)</option>
                <option value="dnc">Do Not Contact</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Phone Type</label>
              <select
                className="form-select"
                value={phoneType}
                onChange={(e) => setPhoneType(e.target.value)}
              >
                <option value="">All Types</option>
                {filterOptions.phone_types.map(type => {
                  const normalized = type || 'unknown';
                  const label = normalized.charAt(0).toUpperCase() + normalized.slice(1);
                  return <option key={normalized} value={normalized}>{label}</option>;
                })}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Phone Country</label>
              <select
                className="form-select"
                value={phoneCountry}
                onChange={(e) => setPhoneCountry(e.target.value)}
              >
                <option value="">All Countries</option>
                {filterOptions.phone_countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Phone Validity</label>
              <select
                className="form-select"
                value={phoneValidity}
                onChange={(e) => setPhoneValidity(e.target.value)}
              >
                <option value="">All Numbers</option>
                <option value="valid">Valid Only</option>
                <option value="invalid">Invalid Only</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Groups</label>
              <GroupSelector
                selectedGroups={selectedGroups}
                onChange={setSelectedGroups}
                multiple={true}
                showNoGroup={true}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Lead Score
                <span style={{
                  marginLeft: '8px',
                  fontSize: '0.85em',
                  color: 'var(--text-secondary)',
                  fontWeight: 'normal'
                }}>
                  (Display Only)
                </span>
              </label>
              <select
                className="form-select"
                value={scoreFilter}
                onChange={(e) => setScoreFilter(e.target.value)}
              >
                <option value="all">All Scores</option>
                <option value="high">High (70-100)</option>
                <option value="medium">Medium (40-69)</option>
                <option value="low">Low (0-39)</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  checked={sortByScore}
                  onChange={(e) => setSortByScore(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                Sort by Score
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', gap: '12px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              style={{ background: 'var(--warn-soft)', borderColor: 'rgba(180, 83, 9, 0.2)', color: 'var(--warn)' }}
              onClick={() => {
                setIndustry('');
                setLocation('');
                setStatus('');
                setContactMethod('');
                setDncStatus('');
                setSelectedGroups([]);
                setScoreFilter('all');
                setSortByScore(false);
                setPhoneType('');
                setPhoneCountry('');
                setPhoneValidity('');
                setHasWebsite('');
              }}
              type="button"
            >
              Clear filters
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-secondary"
                style={{ background: 'var(--danger)', borderColor: 'var(--danger-hover)', color: '#fff' }}
                onClick={() => setShowFilterPanel(false)}
                type="button"
              >
                Close
              </button>
              <button className="btn btn-primary" onClick={() => setShowFilterPanel(false)} type="button">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Action Toolbar */}
      <ProspectsActionToolbar
        selectedCount={selectedBuyers.length}
        selectedBuyerIds={selectedBuyers}
        buyersData={buyers}
        onSendEmail={() => { setOutreachType('email'); setShowOutreachModal(true); }}
        onSendSMS={() => { setOutreachType('sms'); setShowOutreachModal(true); }}
        onVerifyEmails={() => handleBulkVerifyEmails('hunter')}
        onResearchSelected={() => setResearchIds([...selectedBuyers])}
        onMarkDNC={handleMarkDNC}
        onRemoveDNC={handleRemoveDNC}
        onDeleteSelected={handleDeleteSelected}
        onClearSelection={() => setSelectedBuyers([])}
        onGroupActionSuccess={(message) => {
          setSuccess(message);
          setSelectedBuyers([]);
          loadBuyers();
        }}
        bulkVerifying={bulkVerifying}
        onOpenFilter={() => setShowFilterPanel(true)}
        onManageGroups={() => setShowGroupManagement(true)}
        filteredCount={buyers.length}
        totalCount={totalCount}
      />

      <ResearchRunDialog
        isOpen={!!researchIds}
        onClose={() => setResearchIds(null)}
        buyerIds={researchIds || []}
        onQueued={(n) => {
          toast.success(`${n} prospect${n === 1 ? '' : 's'} queued for AI research.`);
          setResearchTick(t => t + 1);
        }}
      />

      {/* Buyers Table */}
      {!loading && !error && filteredBuyers.length > 0 && (
        <div className="card">
          <div className="prospects-view-header">
            <label className="select-all">
              <input
                type="checkbox"
                checked={buyers.length > 0 && selectedBuyers.length === buyers.length}
                onChange={toggleSelectAll}
              />
              <span>Select all</span>
            </label>
          </div>
          {viewMode === 'table' && (
            <div className="table-container">
              <table>
              <thead>
                <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={isPageSelected}
                        onChange={toggleSelectPage}
                      />
                    </th>
                  <th>Business Name</th>
                  <th>Industry</th>
                  <th>Location</th>
                  <th>Email</th>
                  <th>Phone</th>
                  {/* <th>Status</th> */}
                  <th>Groups</th>
                  {/* <th>Lead Score</th> */}
                  <th>DNC</th>
                  <th>Last Contact</th>
                  <th>Method</th>
                  <th>Contact Status</th>
                  <th>Website</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                  {(pagedBuyers || []).map(buyer => {
                  const verificationMeta = getVerificationMeta(buyer.email_verification_status);
                  const phoneMeta = getPhoneTypeMeta(buyer.phone_type);
                  const normalizedPhone = buyer.phone_e164 || buyer.phone || '-';
                  const phoneValidityState = buyer.phone_valid === null || buyer.phone_valid === undefined
                    ? null
                    : Boolean(buyer.phone_valid);
                  return (
                  <tr
                    key={buyer.id}
                    className={selectedBuyers.includes(buyer.id) ? 'selected-row' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedBuyers.includes(buyer.id)}
                        onChange={() => toggleSelectBuyer(buyer.id)}
                      />
                    </td>
                    <td>{buyer.business_name}</td>
                    <td>{buyer.industry}</td>
                    <td>{buyer.location}</td>
                    <td>
                      <div className="email-cell">
                        <span>{buyer.email || '-'}</span>
                        {buyer.email && (
                          <div className="verification-meta">
                            <span className={`verification-badge ${verificationMeta.className}`}>
                              {verificationMeta.label}
                            </span>
                            {typeof buyer.email_verification_score === 'number' && (
                              <span className="verification-score">
                                Score: {buyer.email_verification_score}
                              </span>
                            )}
                            {buyer.last_verified_at && (
                              <span className="verification-date">
                                {formatDateTime(buyer.last_verified_at)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="phone-cell">
                        <span className="phone-number">{normalizedPhone}</span>
                        {(buyer.phone || buyer.phone_e164) && (
                          <div className="phone-meta">
                            <span className={`phone-badge ${phoneMeta.className}`}>
                              {phoneMeta.label}
                            </span>
                            {buyer.phone_country && (
                              <span className="phone-country">Country: {buyer.phone_country}</span>
                            )}
                            {phoneValidityState === false && (
                              <span className="phone-invalid">Invalid</span>
                            )}
                            {canCallBuyer(buyer) ? (
                              <button
                                type="button"
                                className="btn btn-xs btn-link"
                                onClick={() => handleNativeCall(buyer)}
                              >
                                Call Device
                              </button>
                            ) : (
                              <span className="phone-native-disabled" title="Phone invalid or DNC">
                                Calling unavailable
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    {/* <td>
                      <span className={`badge badge-${buyer.status === 'new' ? 'info' : buyer.status === 'contacted' ? 'warning' : 'success'}`}>
                        {buyer.status}
                      </span>
                    </td> */}
                    <td>
                      {buyer.groups && buyer.groups.length > 0 ? (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {buyer.groups.map(group => (
                            <span
                              key={group.id}
                              className="badge badge-info"
                              style={{ backgroundColor: group.color || 'var(--tag)', color: '#fff' }}
                              title={group.name}
                            >
                              {group.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.9em' }}>None</span>
                      )}
                    </td>
                    {/* <td>
                      <div className="lead-score">
                        <div className="score-value">
                          {buyer.leadScore || 0}
                        </div>
                        <div className="score-bar">
                          <div
                            className="score-fill"
                            style={{
                              width: `${buyer.leadScore || 0}%`,
                              backgroundColor: getScoreColor(buyer.leadScore || 0)
                            }}
                          ></div>
                        </div>
                      </div>
                    </td> */}
                    <td>
                      {buyer.do_not_contact ? (
                        <span
                          className="badge badge-danger"
                          title={`Reason: ${buyer.do_not_contact_reason || 'N/A'}\nDate: ${formatDate(buyer.do_not_contact_date)}`}
                          style={{ cursor: 'help' }}
                        >
                          DNC
                        </span>
                      ) : (
                        <span className="badge badge-success">Active</span>
                      )}
                    </td>
                    <td>{formatDate(buyer.last_contact_date)}</td>
                    <td>
                      {buyer.last_contact_method && (
                        <span className="badge badge-info">{buyer.last_contact_method}</span>
                      )}
                    </td>
                    <td>
                      {buyer.last_contact_status ? (
                        <div className="contact-status-cell">
                          <span className={`badge ${getContactStatusBadge(buyer.last_contact_status)}`}>
                            {buyer.last_contact_status}
                          </span>
                          <div className="contact-status-meta">
                            {buyer.last_contact_status_method && (
                              <span>Method: {buyer.last_contact_status_method.toUpperCase()}</span>
                            )}
                            {formatDateTime(buyer.last_contact_status_at) && (
                              <span>{formatDateTime(buyer.last_contact_status_at)}</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>No attempts</span>
                      )}
                    </td>
                    <td>
                      {buyer.website ? (
                        safeUrl(buyer.website) ? (
                          <a
                            href={safeUrl(buyer.website)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-xs btn-link"
                          >
                            Visit Website
                          </a>
                        ) : <span>Visit Website</span>
                      ) : <span className="badge badge-warning">No website</span>}
                      <ProspectExtras buyer={buyer} />
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleVerifyEmail(buyer.id)}
                          disabled={!buyer.email || verifyingBuyerId === buyer.id}
                          title="Verify email using Hunter.io API"
                        >
                          {verifyingBuyerId === buyer.id ? 'Verifying...' : 'Verify Email'}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditBuyer(buyer)}
                          title="Edit buyer"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )})}
                {buyers.length === 0 && (
                  <tr>
                    <td colSpan="15" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      No buyers found. Try adjusting your filters or run a search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}

          <div className="p-list-head" aria-hidden="true">
            <span />
            <div className="p-cols">
              <span>Prospect</span>
              <span>Email</span>
              <span>Phone</span>
              <span>Group</span>
              <span>Status</span>
              <span>Last contact</span>
              <span />
            </div>
          </div>
          <div className="p-list">
              {(pagedBuyers || []).map(buyer => {
                const meta = getProspectMeta(buyer);
                const { verificationMeta, phoneMeta, normalizedPhone, phoneValidityState, statusPill } = meta;
                const isSelected = selectedBuyers.includes(buyer.id);
                const isOpen = openProspectId === buyer.id;
                const groups = Array.isArray(buyer.groups) ? buyer.groups : [];
                const hasPhone = !!(buyer.phone || buyer.phone_e164);
                const lastContact = buyer.last_contact_date ? formatDate(buyer.last_contact_date) : null;
                const name = buyer.business_name || 'Unnamed business';
                return (
                  <div
                    key={buyer.id}
                    className={`p-card${isSelected ? ' selected-row' : ''}${isOpen ? ' is-open' : ''}`}
                  >
                    <label className="p-check">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectBuyer(buyer.id)}
                        aria-label={`Select ${name}`}
                      />
                    </label>
                    <div
                      className="p-row"
                      role="button"
                      tabIndex={0}
                      aria-haspopup="dialog"
                      aria-label={`${name}, view details`}
                      data-prospect-open={buyer.id}
                      onClick={() => openProspect(buyer.id)}
                      onKeyDown={(e) => handleCardKeyDown(e, buyer.id)}
                    >
                      <div className="p-name">
                        <span className="p-title">{name}{renderResearchChip(buyer.id)}</span>
                        <span className="p-sub">
                          {buyer.industry || 'No industry'}
                          {buyer.location ? ` · ${buyer.location}` : ''}
                        </span>
                      </div>
                      <div className="p-meta">
                        <div className="p-email">
                          {buyer.email ? (
                            <>
                              <span className="p-addr" title={buyer.email}>{buyer.email}</span>
                              <span className="p-verif">
                                <Pill tone={verificationMeta.tone} small>{verificationMeta.label}</Pill>
                              </span>
                            </>
                          ) : <span className="p-none">No email</span>}
                        </div>
                        <div className="p-phone">
                          {hasPhone ? (
                            <>
                              <span className="p-phone-num">{normalizedPhone}</span>
                              <span className="p-phone-meta">
                                {phoneMeta.label}
                                {phoneValidityState === false && <span className="p-phone-invalid"> · Invalid</span>}
                              </span>
                            </>
                          ) : <span className="p-none">No phone</span>}
                        </div>
                        <div className="p-group">
                          {groups.length > 0 ? (
                            <>
                              <GroupPill group={groups[0]} />
                              {groups.length > 1 && <GroupMorePill groups={groups.slice(1)} />}
                            </>
                          ) : <span className="p-none">No group</span>}
                        </div>
                        <div className={`p-last${lastContact ? '' : ' p-none'}`}>
                          <span className="p-lbl-m">Last</span>
                          {lastContact || 'Never'}
                        </div>
                      </div>
                      <div className="p-status">
                        <Pill tone={statusPill.tone} title={statusPill.title}>{statusPill.label}</Pill>
                      </div>
                      <div className="p-chev" aria-hidden="true">
                        <span className="p-chev-txt">View</span>
                        <ChevronRight size={16} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          {viewMode === 'cards' && (
            <div className="prospects-cards">
              {(pagedBuyers || []).map(buyer => {
                const verificationMeta = getVerificationMeta(buyer.email_verification_status);
                const phoneMeta = getPhoneTypeMeta(buyer.phone_type);
                const normalizedPhone = buyer.phone_e164 || buyer.phone || '-';
                const phoneValidityState = buyer.phone_valid === null || buyer.phone_valid === undefined
                  ? null
                  : Boolean(buyer.phone_valid);
                return (
                  <div
                    key={buyer.id}
                    className={`prospect-card ${selectedBuyers.includes(buyer.id) ? 'selected-row' : ''}`}
                  >
                    <div className="card-head">
                      <label className="stack-check">
                        <input
                          type="checkbox"
                          checked={selectedBuyers.includes(buyer.id)}
                          onChange={() => toggleSelectBuyer(buyer.id)}
                        />
                      </label>
                      <div className="card-title-wrap">
                        <div className="card-name">{buyer.business_name || 'Unnamed business'}{renderResearchChip(buyer.id)}</div>
                        <div className="card-subtitle">
                          {buyer.industry || 'No industry'}
                          {buyer.location ? ` • ${buyer.location}` : ''}
                        </div>
                      </div>
                      <div className="card-status">
                        {buyer.do_not_contact ? (
                          <span className="badge badge-danger">DNC</span>
                        ) : (
                          <span className="badge badge-success">Active</span>
                        )}
                      </div>
                    </div>

                    <div className="card-quick">
                      {buyer.email ? (
                        <a className="quick-pill" href={`mailto:${buyer.email}`} title="Email">
                          <Mail size={14} />
                          Email
                        </a>
                      ) : (
                        <span className="quick-pill disabled">
                          <Mail size={14} />
                          Email
                        </span>
                      )}
                      {(buyer.phone || buyer.phone_e164) ? (
                        <button
                          type="button"
                          className="quick-pill"
                          onClick={() => handleNativeCall(buyer)}
                          title="Call"
                        >
                          <Phone size={14} />
                          Call
                        </button>
                      ) : (
                        <span className="quick-pill disabled">
                          <Phone size={14} />
                          Call
                        </span>
                      )}
                      {safeUrl(buyer.website) ? (
                        <a
                          className="quick-pill"
                          href={safeUrl(buyer.website)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Website"
                        >
                          <MapPin size={14} />
                          Website
                        </a>
                      ) : (
                        <span className="quick-pill disabled">
                          <MapPin size={14} />
                          Website
                        </span>
                      )}
                    </div>

                    <div className="card-section">
                      <div className="card-field">
                        <span className="stack-label">Email</span>
                        <span className="stack-value">{buyer.email || '-'}</span>
                        {buyer.email && (
                          <div className="verification-meta">
                            <span className={`verification-badge ${verificationMeta.className}`}>
                              {verificationMeta.label}
                            </span>
                            {typeof buyer.email_verification_score === 'number' && (
                              <span className="verification-score">
                                Score: {buyer.email_verification_score}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="card-field">
                        <span className="stack-label">Phone</span>
                        <span className="stack-value">{normalizedPhone}</span>
                        {(buyer.phone || buyer.phone_e164) && (
                          <div className="phone-meta">
                            <span className={`phone-badge ${phoneMeta.className}`}>
                              {phoneMeta.label}
                            </span>
                            {buyer.phone_country && (
                              <span className="phone-country">Country: {buyer.phone_country}</span>
                            )}
                            {phoneValidityState === false && (
                              <span className="phone-invalid">Invalid</span>
                            )}
                            {canCallBuyer(buyer) ? (
                              <button
                                type="button"
                                className="btn btn-xs btn-link"
                                onClick={() => handleNativeCall(buyer)}
                              >
                                Call Device
                              </button>
                            ) : (
                              <span className="phone-native-disabled" title="Phone invalid or DNC">
                                Calling unavailable
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="card-section">
                      <div className="card-field">
                        <span className="stack-label">Groups</span>
                        <div className="stack-value">
                          {buyer.groups && buyer.groups.length > 0 ? (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {buyer.groups.map(group => (
                                <span
                                  key={group.id}
                                  className="badge badge-info"
                                  style={{ backgroundColor: group.color || 'var(--tag)', color: '#fff' }}
                                  title={group.name}
                                >
                                  {group.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.9em' }}>None</span>
                          )}
                        </div>
                      </div>
                      <div className="card-field">
                        <span className="stack-label">Last Contact</span>
                        <span className="stack-value">{formatDate(buyer.last_contact_date)}</span>
                        {buyer.last_contact_method && (
                          <span className="badge badge-info">{buyer.last_contact_method}</span>
                        )}
                      </div>
                      <div className="card-field">
                        <span className="stack-label">Contact Status</span>
                        <div className="stack-value">
                          {buyer.last_contact_status ? (
                            <div className="contact-status-cell">
                              <span className={`badge ${getContactStatusBadge(buyer.last_contact_status)}`}>
                                {buyer.last_contact_status}
                              </span>
                              <div className="contact-status-meta">
                                {buyer.last_contact_status_method && (
                                  <span>Method: {buyer.last_contact_status_method.toUpperCase()}</span>
                                )}
                                {formatDateTime(buyer.last_contact_status_at) && (
                                  <span>{formatDateTime(buyer.last_contact_status_at)}</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>No attempts</span>
                          )}
                        </div>
                      </div>
                      <div className="card-field">
                        <span className="stack-label">Website</span>
                        <span className="stack-value">
                          {buyer.website ? (
                            safeUrl(buyer.website) ? (
                              <a
                                href={safeUrl(buyer.website)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-xs btn-link"
                              >
                                Visit Website
                              </a>
                            ) : <span>Visit Website</span>
                          ) : <span className="badge badge-warning">No website</span>}
                          <ProspectExtras buyer={buyer} />
                        </span>
                      </div>
                    </div>

                    <div className="card-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleVerifyEmail(buyer.id)}
                        disabled={!buyer.email || verifyingBuyerId === buyer.id}
                        title="Verify email using Hunter.io API"
                      >
                        {verifyingBuyerId === buyer.id ? 'Verifying...' : 'Verify Email'}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditBuyer(buyer)}
                        title="Edit buyer"
                      >
                        <Edit size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pagination-bar">
            <div className="pagination-info">
              {totalCount === 0
                ? 'No buyers found'
                : `Showing ${startIndex + 1}–${Math.min(startIndex + buyers.length, totalCount)} of ${totalCount.toLocaleString()} buyers`}
            </div>
            <div className="pagination-controls">
              <label className="page-size">
                <span>Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setCurrentPage(1); }}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </label>
              <button
                type="button"
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={safePage === 1}
              >
                Prev
              </button>
              {pageItems.map(item => (
                item.toString().startsWith('ellipsis') ? (
                  <span key={item} className="page-ellipsis">…</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`page-btn ${safePage === item ? 'active' : ''}`}
                    onClick={() => setCurrentPage(item)}
                  >
                    {item}
                  </button>
                )
              ))}
              <button
                type="button"
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={safePage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prospect detail slide-over */}
      {openBuyer && (
        <ProspectDetailPanel
          key={openBuyer.id}
          buyer={openBuyer}
          meta={getProspectMeta(openBuyer)}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          verifying={verifyingBuyerId === openBuyer.id}
          verifyNotice={getPanelVerifyNotice(openBuyer.id)}
          onVerify={() => { setPanelVerifyFor(openBuyer.id); handleVerifyEmail(openBuyer.id, 'hunter', true); }}
          onCall={() => handleNativeCall(openBuyer)}
          onSave={(changes) => handlePanelSave(openBuyer, changes)}
          onClose={closeProspect}
        />
      )}

      {/* Outreach Modal */}
      {showOutreachModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(18, 15, 40, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 className="card-title">Send {outreachType === 'email' ? 'Email' : 'SMS'}</h3>
            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
              Sending to {selectedBuyers.length} buyer(s)
            </p>

            {outreachType === 'email' ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={handleOpenTemplateSelector}
                    type="button"
                    style={{ width: '100%' }}
                  >
                    Use Template
                  </button>
                </div>
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-input"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Email subject"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-textarea"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Email message"
                    rows="6"
                  />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  className="form-textarea"
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  placeholder="SMS message"
                  rows="4"
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  {smsMessage.length} characters ({Math.ceil(smsMessage.length / 160)} SMS)
                </small>
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                className="btn btn-primary"
                onClick={handleSendOutreach}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowOutreachModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingBuyer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(18, 15, 40, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Edit Buyer</h2>
              <button
                onClick={handleCancelEdit}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Business Name *
                </label>
                <input
                  type="text"
                  value={editingBuyer.business_name || ''}
                  onChange={(e) => setEditingBuyer({ ...editingBuyer, business_name: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-strong)', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Industry
                </label>
                <input
                  type="text"
                  value={editingBuyer.industry || ''}
                  onChange={(e) => setEditingBuyer({ ...editingBuyer, industry: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-strong)', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Location
                </label>
                <input
                  type="text"
                  value={editingBuyer.location || ''}
                  onChange={(e) => setEditingBuyer({ ...editingBuyer, location: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-strong)', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Email
                </label>
                <input
                  type="email"
                  value={editingBuyer.email || ''}
                  onChange={(e) => setEditingBuyer({ ...editingBuyer, email: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-strong)', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={editingBuyer.phone || ''}
                  onChange={(e) => setEditingBuyer({ ...editingBuyer, phone: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-strong)', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Status
                </label>
                <select
                  value={editingBuyer.status || 'new'}
                  onChange={(e) => setEditingBuyer({ ...editingBuyer, status: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-strong)', borderRadius: '4px' }}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Website
                </label>
                <input
                  type="url"
                  value={editingBuyer.website || ''}
                  onChange={(e) => setEditingBuyer({ ...editingBuyer, website: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-strong)', borderRadius: '4px' }}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={editingBuyer.do_not_contact || false}
                    onChange={(e) => setEditingBuyer({ ...editingBuyer, do_not_contact: e.target.checked })}
                  />
                  <span style={{ fontWeight: 500 }}>Do Not Contact</span>
                </label>
              </div>

              {!!editingBuyer.do_not_contact && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    DNC Reason
                  </label>
                  <textarea
                    value={editingBuyer.do_not_contact_reason || ''}
                    onChange={(e) => setEditingBuyer({ ...editingBuyer, do_not_contact_reason: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-strong)', borderRadius: '4px', minHeight: '80px' }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={loading || !editingBuyer.business_name}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Save size={18} />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <TemplateSelector
          type="email"
          buyerData={currentBuyerForTemplate}
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}

      {/* Confirm Dialog */}
      {confirmAsyncDialog}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        variant="danger"
      />

      {/* Mark DNC Modal */}
      {dncModal.open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(18, 15, 40, 0.45)' }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', width: '420px', maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }}>
            <h3 style={{ marginBottom: '8px', fontSize: '16px', fontWeight: 600 }}>Mark as Do Not Contact</h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)', fontSize: '14px' }}>
              Mark {selectedBuyers.length} buyer(s) as Do Not Contact. Enter a reason below (optional).
            </p>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>Reason</label>
            <input
              type="text"
              value={dncModal.reason}
              onChange={(e) => setDncModal(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g. Replied STOP, Unsubscribed, etc."
              autoFocus
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleMarkDNCConfirm(); if (e.key === 'Escape') setDncModal({ open: false, reason: 'Replied STOP' }); }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setDncModal({ open: false, reason: 'Replied STOP' })}
                style={{ padding: '8px 16px', border: '1px solid var(--border-strong)', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleMarkDNCConfirm}
                disabled={loading}
                style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: 'var(--danger)', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                {loading ? 'Marking...' : 'Mark DNC'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Old Bulk Group Actions Bar removed - now integrated into ProspectsActionToolbar above */}

      {/* Group Management Modal */}
      {showGroupManagement && (
        <GroupManagementModal
          onClose={() => setShowGroupManagement(false)}
          onSuccess={(message) => {
            setSuccess(message);
            loadBuyers();
          }}
        />
      )}

      {showCsvResults && csvResults && (
        <CsvUploadResults
          results={csvResults}
          onClose={() => {
            setShowCsvResults(false);
            setCsvResults(null);
          }}
        />
      )}

      {/* Token Top-Up Modal */}
      {showTokenModal && (
        <div
          onClick={() => setShowTokenModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(18, 15, 40, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '16px', padding: '32px',
              width: '100%', maxWidth: '480px', boxShadow: 'var(--shadow-lg)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Buy Credits</h2>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Top-up credits never expire and stack on top of your monthly allowance.
                </p>
              </div>
              <button
                onClick={() => setShowTokenModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.25rem', lineHeight: 1, padding: '2px' }}
              >✕</button>
            </div>

            {tokenBalance && (
              <div style={{ background: 'var(--surface-subtle)', borderRadius: '8px', padding: '10px 14px', marginBottom: '20px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                Current balance: <strong style={{ color: 'var(--text-primary)' }}>{tokenBalance.total.toLocaleString()} credits</strong>
                {tokenBalance.monthly > 0 && ` (${tokenBalance.monthly.toLocaleString()} monthly · ${tokenBalance.bonus.toLocaleString()} top-up)`}
              </div>
            )}

            <TopUpPacks
              variant="list"
              topups={topups}
              loading={plansLoading}
              error={plansError}
              onReload={reloadPlans}
              onSelect={(key) => { setShowTokenModal(false); handleTopUp(key); }}
              disabled={toppingUp}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default BuyersList;
